import { useState, useRef, useEffect, useMemo } from 'react';
import {
  StatusType,
  GPTErrorStatusType,
  GptOptionsType,
  GptRequestType,
  PromptConfirmItemType,
} from '../../types';
import { mdContentToHtml, replaceGptText } from '@op/utils/text';
import { getLocale, useIntl } from 'umi';
import { LANGUAGE_CN } from '@home/_config/config';
import { LocaleType, SentimentType } from '@op/types/common';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

type PageStatusType = { status: StatusType; tips?: GPTErrorStatusType };

export type CommonGptDataType = {
  stopAction?: () => void;
  successAction?: () => void;
  backGPTText?: (text: string | GptRequestType) => void;
  errorCallback?: () => void;
  defaultGptText?: string;
  promptInfo?: PromptConfirmItemType;
  showDefaultGpt?: boolean;
  // 外部传入的html
  // gptDefaultHtml?: string;
  // 外部获取gptSummary数据，用于studio编辑弹窗
  getGptSummaryData?: (data: {
    id: string | undefined;
    gpt_summary: string;
  }) => void;
  lessThanIsEmpty?: number;
  defaultGPT?: any;
  language?: LocaleType;
};

export default function useCommonGptData({
  stopAction,
  backGPTText,
  errorCallback,
  defaultGptText = '',
  promptInfo,
  showDefaultGpt = false,
  getGptSummaryData,
  lessThanIsEmpty = 20,
  defaultGPT,
  successAction,
  language,
}: CommonGptDataType) {
  const { formatMessage } = useIntl();
  const [percent, setPercent] = useState(1);
  const localePercent = useRef(0);
  function changeLocalPercent(value: number) {
    localePercent.current = value;
  }
  const [pageStatus, setPageStatus] = useState<PageStatusType>({
    status: 'loading',
  });

  const [gptOptions, setGptOptions] = useState<GptOptionsType>(
    promptInfo?.gptOptions || 'basic',
  );
  const [origSummary, setOrigSummary] = useState<string | GptRequestType>(
    defaultGptText,
  );
  const stopRef = useRef<any>(null);
  const stopTarget = useRef(false);
  const interval: any = useRef(null);

  useEffect(() => {
    if (showDefaultGpt) {
      setOrigSummary(defaultGptText);
    }
  }, [defaultGptText, showDefaultGpt]);

  function handleStop() {
    stopTarget.current = true;
    stopRef.current?.();
    initState();
    stopAction?.();
    buriedBtnPoint('GPT_Stop');
  }

  function initState(defaultSummary = '') {
    stopRef.current = null;
    changeLocalPercent(0);
    setPercent(0);
    setOrigSummary(defaultSummary);
    backGPTText?.(defaultSummary);
    clearInterval(interval.current);
  }

  function handleSuccess(summary: string | GptRequestType) {
    clearInterval(interval.current);
    setPercent(100);
    backGPTText?.(summary);
    setOrigSummary(summary);
    setPageStatus({ status: 'success' });
    successAction?.();
  }

  function handleError(tips?: GPTErrorStatusType) {
    clearInterval(interval.current);
    setPercent(100);
    setPageStatus({ status: 'failed', tips });
    errorCallback?.();
  }

  function loadingBefore(
    promptInfo: PromptConfirmItemType,
    defaultPercent = 0,
  ) {
    // 这两个值不能放在initState里面，否则会导致无法获取到cancel状态
    setPageStatus({ status: 'loading' });
    stopTarget.current = false;
    initState();
    const isAdvanced = promptInfo.gptOptions === 'advanced';
    setGptOptions(promptInfo.gptOptions || 'basic');
    changeLocalPercent(defaultPercent);
    interval.current = setInterval(
      () => {
        const value = Math.min(localePercent.current + 1, 99);
        changeLocalPercent(value);
        setPercent(value);
      },
      isAdvanced ? 2000 : 1000,
    );

    return {
      stop: stopRef,
      cancelCallback: () => stopTarget.current,
    };
  }

  const { renderHtml, isEmpty, summary, realStatus, errorType, GPTSentiment } =
    useMemo((): {
      renderHtml: string | null;
      isEmpty: boolean;
      summary: string;
      realStatus: StatusType;
      errorType?: GPTErrorStatusType;
      GPTSentiment?: SentimentType;
    } => {
      const { status, tips } = pageStatus;

      if (!origSummary) {
        getGptSummaryData?.({
          id: undefined,
          gpt_summary: '',
        });
        return {
          renderHtml: null,
          isEmpty: true,
          summary: '',
          realStatus: status,
          errorType: tips,
        };
      }

      let summaryText: string = origSummary as string;
      let GPTSentiment: SentimentType | undefined = undefined;
      if (typeof origSummary !== 'string') {
        const local = language || getLocale();
        const reportEN = origSummary?.report_en || origSummary?.report_zh;
        const reportCN = origSummary?.report_zh || origSummary?.report_en;
        summaryText = local === LANGUAGE_CN ? reportCN : reportEN;
        GPTSentiment = origSummary?.sentiment;
      }
      const replacedSummary = replaceGptText(summaryText);

      const { html } = mdContentToHtml(replacedSummary);

      getGptSummaryData?.({
        // 设置为undefined，避免是指为0 或者 "0" 时、刚好和后端正常数据重复
        id: defaultGPT?.id ? defaultGPT?.id : undefined,
        gpt_summary: html,
      });
      // 有默认gptSummary，则认为不是空的
      const isEmpty =
        replacedSummary.length < lessThanIsEmpty && status !== 'failed';

      return {
        renderHtml: html,
        isEmpty,
        summary: replacedSummary,
        realStatus: isEmpty ? 'failed' : status,
        errorType: isEmpty ? 'GPT_nodata' : tips,
        GPTSentiment,
      };
    }, [
      origSummary,
      defaultGPT,
      lessThanIsEmpty,
      formatMessage,
      pageStatus,
      language,
    ]);

  useEffect(() => {
    return () => {
      handleStop();
    };
  }, []);

  return {
    status: realStatus,
    percent,
    renderHtml,
    isEmpty,
    handleStop,
    summary,
    errorType,
    GPTSentiment,
    handleSuccess,
    handleError,
    setPageStatus,
    loadingBefore,
    origSummary,
  };
}
